import React from "react";
import { Loader2 } from "lucide-react";

// ── Spinner ─────────────────────────────────────────────────
export function Spinner({ size = 20, className = "" }) {
  return <Loader2 size={size} className={`animate-spin text-brand-500 ${className}`} />;
}

// ── Button ───────────────────────────────────────────────────
export function Button({ children, variant = "primary", size = "md", loading = false, disabled = false, className = "", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 font-bold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 select-none";
  const sizes = { sm: "px-3 py-1.5 text-sm", md: "px-5 py-2.5 text-base", lg: "px-7 py-3.5 text-lg" };
  const variants = {
    primary: "bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white focus:ring-brand-400 shadow-md hover:shadow-lg",
    secondary: "bg-white hover:bg-brand-50 active:bg-brand-100 text-brand-700 border-2 border-brand-200 focus:ring-brand-300",
    danger: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-400",
    ghost: "bg-transparent hover:bg-brand-50 text-brand-700 focus:ring-brand-300",
    earth: "bg-earth-500 hover:bg-earth-600 text-white focus:ring-earth-300 shadow-md",
  };
  return (
    <button
      className={`${base} ${sizes[size]} ${variants[variant]} ${(disabled || loading) ? "opacity-60 cursor-not-allowed" : ""} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner size={16} className="text-inherit opacity-70" />}
      {children}
    </button>
  );
}

// ── Input ────────────────────────────────────────────────────
export function Input({ label, error, icon: Icon, className = "", ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-sm font-700 text-brand-800">{label}</label>}
      <div className="relative">
        {Icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-400"><Icon size={18} /></span>}
        <input
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 py-3 rounded-xl border-2 ${error ? "border-red-400 bg-red-50" : "border-brand-200 bg-white"} text-brand-900 placeholder-brand-300 focus:outline-none focus:border-brand-500 transition-colors font-600 text-base ${className}`}
          {...props}
        />
      </div>
      {error && <p className="text-xs text-red-500 font-600">{error}</p>}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────
export function Card({ children, className = "", onClick, hover = false }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border border-brand-100 ${hover ? "hover:shadow-md hover:border-brand-300 transition-all cursor-pointer" : ""} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// ── Badge ────────────────────────────────────────────────────
export function Badge({ children, color = "green" }) {
  const colors = {
    green: "bg-brand-100 text-brand-800 border border-brand-200",
    red: "bg-red-100 text-red-800 border border-red-200",
    yellow: "bg-earth-100 text-earth-800 border border-earth-200",
    gray: "bg-gray-100 text-gray-700 border border-gray-200",
    blue: "bg-blue-100 text-blue-800 border border-blue-200",
    purple: "bg-purple-100 text-purple-800 border border-purple-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-800 ${colors[color]}`}>
      {children}
    </span>
  );
}

// ── Alert ────────────────────────────────────────────────────
export function Alert({ type = "info", children, onDismiss }) {
  const styles = {
    success: "bg-brand-50 border-brand-300 text-brand-800",
    error: "bg-red-50 border-red-300 text-red-800",
    warning: "bg-earth-50 border-earth-300 text-earth-800",
    info: "bg-blue-50 border-blue-300 text-blue-800",
  };
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border-2 text-sm font-600 ${styles[type]}`}>
      <span className="flex-1">{children}</span>
      {onDismiss && <button onClick={onDismiss} className="opacity-60 hover:opacity-100 font-800 text-lg leading-none">×</button>}
    </div>
  );
}

// ── Status Badge ─────────────────────────────────────────────
export function StatusBadge({ status }) {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === "healthy") return <Badge color="green">✅ Healthy</Badge>;
  if (s === "sick") return <Badge color="red">🔴 Sick</Badge>;
  return <Badge color="yellow">🔍 Further Review</Badge>;
}

// ── Modal ────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-slide-up">
        {title && <h3 className="text-xl font-800 text-brand-800 mb-4">{title}</h3>}
        <button onClick={onClose} className="absolute top-4 right-4 text-brand-400 hover:text-brand-700 font-800 text-xl">×</button>
        {children}
      </div>
    </div>
  );
}

// ── Page Header ───────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-900 text-brand-800">{title}</h1>
        {subtitle && <p className="text-brand-500 font-600 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
      <div className="text-6xl">{icon}</div>
      <div>
        <h3 className="text-xl font-800 text-brand-700">{title}</h3>
        <p className="text-brand-400 font-600 mt-1 max-w-xs mx-auto">{description}</p>
      </div>
      {action}
    </div>
  );
}
